/**
 * Christmas tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasTattoosPack: ToilePack = {
    slug: 'christmas-tattoos',
    category: 'season',
    name: 'Christmas tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'holly', label: 'Holly sprig', subject: 'A small holly sprig with berries'},
        {key: 'snowflake', label: 'Snowflake', subject: 'A single six-pointed snowflake'},
        {key: 'ornament', label: 'Ornament', subject: 'A small round ornament on a ribbon'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'fine single-needle black line work, thin uniform stroke, no shading, no color', palette: 'Black ink only', framing: 'Small scale, centered with a wide white margin'},
        {key: 'traditional', label: 'Bold traditional', style: 'bold American traditional flash-sheet outline, thick uniform black line, no shading, no color', palette: 'Black ink only', framing: 'Centered with a wide white margin, classic flash-sheet scale'},
        {key: 'dotwork', label: 'Dotwork', style: 'fine dotwork stippled shading over a thin black outline, stippled dot-gradient fill, no color', palette: 'Black ink, stippled dot shading', framing: 'Centered with a wide white margin'},
    ],
    directions: ['a matching pair design', 'as a tiny minimalist mark', 'a wreath instead'],
    directionHint: 'try: as a matching pair design',
    seo: {
        title: 'Christmas Tattoo Ideas: Holly, Snowflake, Ornament',
        metaDescription: 'Nine christmas tattoo designs — a holly sprig, a snowflake, a small ornament — fine line, black ink, no skin shown, three styles.',
        h1: 'Christmas tattoo ideas: holly, snowflake, ornament',
        tagline: 'Nine designs, three December subjects, three styles each.',
        intro: 'A flash sheet of small December tattoo ideas: a holly sprig, a snowflake, a small ornament, drawn fine-line in black ink with no skin shown. Ask for a pine branch, a mistletoe sprig, or your own subject, and the same three styles carry it.',
        useCases: [
            'a reference to bring to a tattoo artist',
            "a small flash sheet for a shop's holiday board",
            'a temporary-tattoo design for a party',
            'an idea board for a seasonal piece',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine tattoo-style designs — a holly sprig, a snowflake, a small ornament — fine-line black ink on a plain background, no skin, in three styles.'},
            {q: 'Is this an actual tattoo, or a design reference?', a: "A design reference — it's artwork to bring to or discuss with a tattoo artist, not a photo of a real tattoo."},
            {q: 'Can I use this as a stencil for tattooing?', a: 'The line weight and detail are built for the design stage; a tattoo artist will typically adapt it for their own stencil and skin placement.'},
            {q: 'Can I get a different subject, like mistletoe?', a: 'Yes — the direction field takes any Christmas subject, including a mistletoe sprig or a pine branch.'},
            {q: 'Can a tattoo shop use these designs on its flash sheet?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
